import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../../../state'
import * as shared from '../../../../../shared'

/** Identifies this report to the configuration plugin. */
const REPORT_ID = ents.extra.space.Report.Id.RoomSupportingInformation

/**
 * Page content for the "Room Supporting Information" space report.
 */
@ngCore.Component({
  imports: [
    ds.input.checkbox.Component,
    ds.input.select.multiple.Component,
    ds.input.select.single.Component,
    ds.layout.section.Component,
    ds.layout.widget.Component,
    shared.dimensionFilter.Component,
  ],
  selector: 'atn-space-report-room-supporting-information',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.reports.Collection} */
  private readonly _reportsEc = ngCore.inject(state.space.reports.Collection)

  /** @see {@link state.space.reports.plugins.configuration.Plugin} */
  public readonly configurationPlugin = this._reportsEc.plugins
    .lookup(state.space.reports.plugins.configuration.Plugin)
    .get()

  /** @see {@link state.space.reports.plugins.clusterDimensionFilter.Plugin} */
  public readonly clusterFilterPlugin = this._reportsEc.plugins
    .lookup(state.space.reports.plugins.clusterDimensionFilter.Plugin)
    .get()

  /** @see {@link state.space.reports.plugins.organizationDimensionFilter.Plugin} */
  public readonly organizationFilterPlugin = this._reportsEc.plugins
    .lookup(state.space.reports.plugins.organizationDimensionFilter.Plugin)
    .get()

  /** @see {@link state.space.reports.plugins.employeeDimensionFilter.Plugin} */
  public readonly employeeFilterPlugin = this._reportsEc.plugins
    .lookup(state.space.reports.plugins.employeeDimensionFilter.Plugin)
    .get()

  /** @see {@link state.space.reports.plugins.roomTypeDimensionFilter.Plugin} */
  public readonly roomTypeFilterPlugin = this._reportsEc.plugins
    .lookup(state.space.reports.plugins.roomTypeDimensionFilter.Plugin)
    .get()

  /** @see {@link state.space.reports.plugins.spaceDimensionFilter.Plugin} */
  public readonly spaceFilterPlugin = this._reportsEc.plugins
    .lookup(state.space.reports.plugins.spaceDimensionFilter.Plugin)
    .get()

  /** Configuration settings for this report */
  public readonly configurationSg =
    this.configurationPlugin.selectors.getSettings(REPORT_ID).asSignal

  /** @see {@link state.space.reports.plugins.configuration.Plugin.selectors.isStandardUser} */
  public readonly isStandardUserSg =
    this.configurationPlugin.selectors.isStandardUser.asSignal

  /** Options for the sort by select. */
  public readonly sortByItems: readonly ds.input.select.models.Item<ents.extra.space.Report.Configuration.RoomSupportingInformation.SortBy>[] =
    ents.extra.space.Report.Configuration.RoomSupportingInformation.SortBy.ALL.map(
      sortBy => ({
        label:
          ents.extra.space.Report.Configuration.RoomSupportingInformation.SortBy.getLabel(
            sortBy,
          ),
        value: sortBy,
      }),
    )

  /** Options for the format select. */
  public readonly formatItems: readonly ds.input.select.models.Item<ents.extra.space.Report.Format>[] =
    ents.extra.space.Report.Format.SUPPORTED.map(format => ({
      label: ents.extra.space.Report.Format.getLabel(format),
      value: format,
    }))

  /**
   * Options for the functional methodology select.
   *
   * NOTE: methodology values are already display-ready strings, so they double
   * as their own labels.
   */
  public readonly methodologyItems: readonly ds.input.select.models.Item<ents.extra.space.Record.FunctionalUsage.Methodology>[] =
    ents.extra.space.Record.FunctionalUsage.Methodology.ALL.map(
      methodology => ({
        label: methodology,
        value: methodology,
      }),
    )

  /** Handles a change to the sort by select, ignoring a cleared selection. */
  public sortByChangeHandler(
    value:
      | ents.extra.space.Report.Configuration.RoomSupportingInformation.SortBy
      | undefined,
  ): void {
    if (value) {
      this.configurationPlugin.actions.edit
        .from(
          'SpaceReport.RoomSupportingInformation.Configuration.SortBy.Changed',
        )
        .dispatch({ edit: { sortBy: value }, id: REPORT_ID })
    }
  }

  /**
   * Handles a change to the format select.
   *
   * NOTE: format is required, so a cleared selection is ignored to keep the
   * last valid value.
   */
  public formatChangeHandler(
    value: ents.extra.space.Report.Format | undefined,
  ): void {
    if (value) {
      this.configurationPlugin.actions.edit
        .from(
          'SpaceReport.RoomSupportingInformation.Configuration.Format.Changed',
        )
        .dispatch({ edit: { format: value }, id: REPORT_ID })
    }
  }

  /** Handles a change to the functional methodology select. */
  public methodologiesChangeHandler(
    values: readonly (
      | ents.extra.space.Record.FunctionalUsage.Methodology
      | undefined
    )[],
  ): void {
    this.configurationPlugin.actions.edit
      .from(
        'SpaceReport.RoomSupportingInformation.Configuration.Methodologies.Changed',
      )
      .dispatch({
        edit: {
          filter: {
            functionalMethodologies: values.filter(
              TypeChecker.isNotNullish.raw,
            ),
          },
        },
        id: REPORT_ID,
      })
  }

  /** Handles a change to the "Show Employee Numbers" checkbox. */
  public showEmployeeNumbersChangeHandler(value: boolean): void {
    this.configurationPlugin.actions.edit
      .from(
        'SpaceReport.RoomSupportingInformation.Configuration.ShowEmployeeNumbers.Changed',
      )
      .dispatch({ edit: { showEmployeeNumbers: value }, id: REPORT_ID })
  }

  /** Handles a change to the "Show Salary Information" checkbox. */
  public showSalaryInformationChangeHandler(value: boolean): void {
    this.configurationPlugin.actions.edit
      .from(
        'SpaceReport.RoomSupportingInformation.Configuration.ShowSalaryInformation.Changed',
      )
      .dispatch({ edit: { showSalaryInformation: value }, id: REPORT_ID })
  }

  /** Handles a change to the "Show Calculated Room Functional Percentages" checkbox. */
  public showCalculatedRoomFunctionalPercentagesChangeHandler(
    value: boolean,
  ): void {
    this.configurationPlugin.actions.edit
      .from(
        'SpaceReport.RoomSupportingInformation.Configuration.ShowCalculatedRoomFunctionalPercentages.Changed',
      )
      .dispatch({
        edit: { showCalculatedRoomFunctionalPercentages: value },
        id: REPORT_ID,
      })
  }

  /** Handles a change to the "Show Account Information" checkbox. */
  public showProjectInformationChangeHandler(value: boolean): void {
    this.configurationPlugin.actions.edit
      .from(
        'SpaceReport.RoomSupportingInformation.Configuration.ShowProjectInformation.Changed',
      )
      .dispatch({ edit: { showProjectInformation: value }, id: REPORT_ID })
  }
}
